import { useEffect, useMemo, useRef, useState } from "react";
import Button from "@/shared/components/Button";
import { Empty, EmptyContent } from "@/shared/components/ui/empty";
import { Spinner } from "@/shared/components/ui/spinner";
import {
  buildEducationPortalAuthUrl,
  openEducationPortalWindow,
  useEducationAuth,
} from "@/features/admin/groups/learning/services/education";

export default function CertificatesAndAchievement() {
  const { data, isLoading, isFetching, isError, refetch } = useEducationAuth();
  const [authStatus, setAuthStatus] = useState<"idle" | "opening" | "success">(
    "idle"
  );
  const hasAttemptedAuth = useRef(false);
  const authUrl = useMemo(() => buildEducationPortalAuthUrl(data), [data]);

  const isBusy = isLoading || isFetching;

  useEffect(() => {
    // Two-step redirect: 1) Authenticate, 2) Redirect to certificates page
    if (authUrl && !hasAttemptedAuth.current && authStatus === "idle") {
      hasAttemptedAuth.current = true;

      setTimeout(() => {
        setAuthStatus("opening");

        const TARGET_CERT_URL =
          "https://nommiaedu.thebetaprojects.com/profile-setting/certificates_and_achievements";

        openEducationPortalWindow(authUrl, TARGET_CERT_URL);
        setTimeout(() => {
          setAuthStatus("success");
        }, 2000); // Wait 2 seconds for authentication
      }, 100);
    }
  }, [authUrl, authStatus]);

  const handleRetry = () => {
    hasAttemptedAuth.current = false;
    setAuthStatus("idle");
    void refetch();
  };

  const handleOpenPortal = () => {
    if (authUrl) {
      const TARGET_CERT_URL =
        "https://nommiaedu.thebetaprojects.com/profile-setting/certificates_and_achievements";
      openEducationPortalWindow(authUrl, TARGET_CERT_URL);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-100px)] items-center justify-center p-6">
      {isBusy ? (
        <div className="flex flex-col items-center gap-4">
          <Spinner className="size-10 text-primary-300" />
          <p className="text-sm text-white/60">
            Fetching authentication credentials...
          </p>
        </div>
      ) : isError ? (
        <Empty className="min-h-[420px] border border-white/10 bg-transparent">
          <EmptyContent>
            <p className="mb-4 text-sm text-white/60">
              Failed to fetch authentication credentials
            </p>
            <Button variant="secondary" onClick={handleRetry}>
              Retry
            </Button>
          </EmptyContent>
        </Empty>
      ) : authStatus === "opening" ? (
        <div className="flex flex-col items-center gap-4">
          <Spinner className="size-10 text-primary-300" />
          <p className="text-sm text-white/60">Opening certificates...</p>
        </div>
      ) : authStatus === "success" ? (
        <Empty className="min-h-[420px] border border-white/10 bg-transparent">
          <EmptyContent>
            <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-green-500/10">
              <svg
                className="size-8 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="mb-2 text-base font-medium text-white">
              Certificates & Achievements Opened!
            </p>
            <p className="mb-4 text-sm text-white/60">
              The page has been opened in a new tab. If it didn't open, your
              browser may have blocked the popup. Click the button below to try
              again.
            </p>
            <Button variant="secondary" onClick={handleOpenPortal}>
              Open Certificates
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <Empty className="min-h-[420px] border border-white/10 bg-transparent">
          <EmptyContent>
            <p className="mb-4 text-sm text-white/60">
              Unable to generate authentication URL
            </p>
            <Button variant="secondary" onClick={handleRetry}>
              Refresh
            </Button>
          </EmptyContent>
        </Empty>
      )}
    </div>
  );
}
